// react
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';

// material ui
import { Box, Tooltip, Typography } from '@mui/material';
// types
import type { GridColDef } from '@mui/x-data-grid-premium';

// api
import { useExtractBenchmarkDataBenchmarkAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/benchmarking';
import { useGetMatrikelAnalysisMatrikelAnalysisAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMatrikel';

// shared components
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import OccupancyDaysCell from '@/features/energyxplorer/portfolio/components/datagrid/cells/OccupancyDaysCell';
import TypeIcon from '@/features/energyxplorer/portfolio/components/datagrid/cells/TypeIcon';
// components
import type { PortfolioAnalysisOutput } from '@/features/energyxplorer/types';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

// relative
import { formatNumber } from '../../../../utils/formatters';
import BenchmarkChip from '../components/datagrid/cells/benchmark-graph/BenchmarkChip';
import EnergyMark from '../components/datagrid/cells/EnergyMark';
import {
  CustomHeaderWithLabel,
  displayTime,
  getOverwrite,
  isGroupRow,
  isLeafRow,
  valueGetterWrapper,
} from '../utils/datagrid-helpers';

export const useDashboardColumns = (
  transformedData:
    | (PortfolioAnalysisOutput & {
        portfolio_analysis_list: Array<Record<string, unknown>>;
      })
    | null
    | undefined,
  preset: Record<string, unknown>,
): GridColDef[] => {
  const { t } = useTranslation('portfolio-analysis_dashboard');
  const firstColumns = useMemo(() => {
    const result = (
      preset.columnGroupingModel as Array<{
        children: Array<{ field?: string }>;
      }>
    ).map((group) => {
      return group.children[0]?.field;
    });
    result.push('warnings');
    return result;
  }, [preset]);
  const account = useSelector(getSelectedAccount);

  const { data: matrikelAnalysisData } =
    useGetMatrikelAnalysisMatrikelAnalysisAccountIdGetQuery(
      { accountId: account?.AccountId },
      { skip: !account?.AccountId },
    );
  const { data: benchmarkData } =
    useExtractBenchmarkDataBenchmarkAccountIdGetQuery(
      {
        accountId: account?.AccountId,
      },
      {
        skip: !account?.AccountId,
      },
    );
  const columns = useMemo(() => {
    const cols: GridColDef[] = [
      {
        flex: 0,
        aggregable: false,
        sortable: false,
        disableColumnMenu: true,
        hideSortIcons: true,
        field: 'favorites',
        type: 'boolean',
        minWidth: 40,
        width: 40,
        renderCell: (params) => {
          if (params.rowNode.type === 'leaf') {
            return null;
          }
          return !params.value ? (
            <StarBorderRoundedIcon
              sx={{ minWidth: 50, cursor: 'pointer' }}
              color="primary"
            />
          ) : (
            <StarRoundedIcon
              sx={{ minWidth: 50, cursor: 'pointer' }}
              color="warning"
            />
          );
        },
      },
      {
        field: 'matrikel_key',
        type: 'string',
        aggregable: false,
        groupingValueGetter: (params) => {
          let name = '';
          if (params == null) return null;

          transformedData.portfolio_analysis_list.filter((item) => {
            if (item.matrikel_key === params) {
              name = item.matrikel_key;
            }
            return item.matrikel_key === params;
          });
          return name;
        },
        hideable: false,
      },
      {
        field: 'type',
        sortable: false,
        disableColumnMenu: true,
        headerName: 'SEU',
        minWidth: 40,
        width: 40,
        flex: 0,
        renderCell: (params) => <TypeIcon params={params} />,
      },
      {
        field: 'main_address',
        type: 'string',
        minWidth: 250,
        aggregable: false,
        hideable: false,
      },
      {
        field: 'address',
        minWidth: 250,
        type: 'string',
        hideable: false,
        aggregable: false,
        valueGetter: (value, row) => {
          return row.DisplayName || value;
        },
      },
      {
        field: 'power_total_consumption_year',
        valueFormatter: (params) => {
          if (params == null || params === 0) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'heat_total_consumption_year',
        valueFormatter: (params) => {
          if (params == null || params === 0) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'water_total_consumption_year',
        valueFormatter: (params) => {
          if (params == null || params === 0) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'power_current_calendar_year_sum',
        valueFormatter: (params) => {
          if (params == null || params === 0) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'heat_current_calendar_year_sum',
        valueFormatter: (params) => {
          if (params == null || params === 0) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'water_current_calendar_year_sum',
        valueFormatter: (params) => {
          if (params == null || params === 0) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'occupancy',
        minWidth: 120,
        maxWidth: 120,
        type: 'string',
        aggregable: false,
        renderCell: (params) => {
          return <OccupancyDaysCell daysOfWeek={params.value} />;
        },
      },
      {
        field: 'daytime_start',
        minWidth: 80,
        type: 'number',
        valueFormatter: (value) => displayTime(value),
        renderCell: (params) => {
          return (
            <Box
              sx={{
                fontWeight:
                  params?.row?.daytime_period_source === 'manual' ? 600 : 400,
              }}
            >
              {params.formattedValue}
            </Box>
          );
        },
      },
      {
        field: 'daytime_end',
        minWidth: 80,
        type: 'number',
        valueFormatter: (value) => displayTime(value),
        renderCell: (params) => {
          return (
            <Box
              sx={{
                fontWeight:
                  params?.row?.daytime_period_source === 'manual' ? 600 : 400,
              }}
            >
              {params.formattedValue}
            </Box>
          );
        },
      },
      {
        field: 'power_consumptionPrYPrM2',
        maxWidth: 120,
        minWidth: 120,
        valueGetter: (value, row) => {
          const entry = matrikelAnalysisData?.MatrikelAnalysis?.find(
            (x) => x.matrikel_key === row.matrikel_key,
          );
          return entry?.matrikel_power?.consumptionPrM2PrY ?? null;
        },
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        renderCell: (params) => {
          const matrikelKey = params.row.matrikel_key as string;

          return (
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <BenchmarkChip
                benchmarkData={benchmarkData?.BenchmarkData}
                matrikelKey={matrikelKey}
                energyType="El"
              />
              {params.formattedValue}
            </Box>
          );
        },
      },
      {
        field: 'heat_consumptionPrYPrM2',
        maxWidth: 100,
        minWidth: 100,
        valueGetter: (value, row) => {
          const entry = matrikelAnalysisData?.MatrikelAnalysis?.find(
            (x) => x.matrikel_key === row.matrikel_key,
          );
          return entry?.matrikel_heat?.consumptionPrM2PrY ?? null;
        },
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'water_consumptionPrYPrM2',
        maxWidth: 100,
        minWidth: 100,
        valueGetter: (value, row) => {
          const entry = matrikelAnalysisData?.MatrikelAnalysis?.find(
            (x) => x.matrikel_key === row.matrikel_key,
          );
          return entry?.matrikel_water?.consumptionPrM2PrY ?? null;
        },
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'AverageBuildingYear',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }

            const matrikelData = matrikelAnalysisData?.MatrikelAnalysis?.find(
              (item) => {
                return item.matrikel_key === row.matrikel_key;
              },
            );

            const { value, isOverwritten } = getOverwrite(
              'AverageBuildingYear',
              matrikelData,
            );

            let formattedValue = '-';
            if (value && value !== -Infinity) {
              try {
                formattedValue = value.toFixed(0);
              } catch (e) {
                console.error('Error formatting AverageBuildingYear:', e);
                formattedValue = value;
              }
            }

            return (
              <Box
                sx={{
                  fontWeight: isOverwritten ? 600 : 400,
                }}
              >
                {formattedValue}
              </Box>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'SumBuildingArea',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            const matrikelData = matrikelAnalysisData?.MatrikelAnalysis?.find(
              (item) => {
                return item.matrikel_key === row.matrikel_key;
              },
            );

            const { value, isOverwritten } = getOverwrite(
              'SumBuildingArea',
              matrikelData,
            );

            return (
              <Box
                sx={{
                  fontWeight: isOverwritten ? 600 : 400,
                }}
              >
                {formatNumber(value)}
              </Box>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'ExtractedBuildingUse',
        type: 'string',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            const matrikelData = matrikelAnalysisData?.MatrikelAnalysis?.find(
              (item) => {
                return item.matrikel_key === row.matrikel_key;
              },
            );

            const { value, isOverwritten } = getOverwrite(
              'ExtractedBuildingUse',
              matrikelData,
            );

            row = {
              ...row,
              BuildingUsesWithArea: matrikelData?.BuildingUsesWithArea,
            };
            if (row?.BuildingUsesWithArea == undefined) return null;

            const jsonObject = JSON.parse(
              row?.BuildingUsesWithArea?.replaceAll("'", '"'),
            );
            const result = Object.entries(jsonObject).map(([key, value]) => ({
              key,
              value,
            }));
            if (result.length === 1)
              return (
                <Box
                  sx={{
                    fontWeight: isOverwritten ? 600 : 400,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {value}
                </Box>
              );
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: isOverwritten ? 600 : 400,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {value}
                  </Box>
                </div>
                <InfoTooltipIcon
                  title={
                    <>
                      {result.map((buildingType, index) => (
                        <div key={index}>
                          <Typography variant="body2">
                            {buildingType.key}: {buildingType.value}{' '}
                            {t('hooks.square_meter', { ns: 'energyxplorer' })}
                          </Typography>
                        </div>
                      ))}
                    </>
                  }
                />
              </div>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'ExtractedHeatInstallation',
        type: 'string',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            const matrikelData = matrikelAnalysisData?.MatrikelAnalysis?.find(
              (item) => {
                return item.matrikel_key === row.matrikel_key;
              },
            );
            const { value, isOverwritten } = getOverwrite(
              'ExtractedHeatInstallation',
              matrikelData,
            );
            row = {
              ...row,
              HeatInstallationsWithArea:
                matrikelData?.HeatInstallationsWithArea,
            };
            if (row?.HeatInstallationsWithArea == undefined) return null;

            const jsonObject = JSON.parse(
              row?.HeatInstallationsWithArea?.replaceAll("'", '"'),
            );
            const result = Object.entries(jsonObject).map(([key, value]) => ({
              key,
              value,
            }));
            if (result.length === 1)
              return (
                <Box
                  sx={{
                    fontWeight: isOverwritten ? 600 : 400,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {value}
                </Box>
              );
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: isOverwritten ? 600 : 400,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {value}
                  </Box>
                </div>
                <InfoTooltipIcon
                  title={
                    <>
                      {result.map((buildingType, index) => (
                        <div key={index}>
                          <Typography variant="body2">
                            {buildingType.key}: {buildingType.value}{' '}
                            {t('hooks.square_meter', { ns: 'energyxplorer' })}
                          </Typography>
                        </div>
                      ))}
                    </>
                  }
                />
              </div>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'EnergyLabelClassification',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }

            const matrikelData = matrikelAnalysisData?.MatrikelAnalysis?.find(
              (item) => {
                return item.matrikel_key === row.matrikel_key;
              },
            );
            row = {
              ...row,
              EnergyLabelCountOverview: matrikelData?.EnergyLabelCountOverview,
            };
            row = {
              ...row,
              EnergyLabelClassification:
                matrikelData?.EnergyLabelClassification,
            };
            if (
              row.EnergyLabelCountOverview == null ||
              row.EnergyLabelClassification == null
            )
              return null;

            const jsonObject = JSON.parse(
              row?.EnergyLabelCountOverview?.replaceAll("'", '"'),
            );
            const result = Object.entries(jsonObject).map(([key, value]) => ({
              key,
              value,
            }));
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <EnergyMark mark={row?.EnergyLabelClassification} />
                {result.length > 1 && (
                  <InfoTooltipIcon
                    title={
                      <>
                        <Typography variant="body1">
                          {t('tooltips.energymarksFound')}
                        </Typography>
                        {result.map((buildingType, index) => (
                          <div key={index}>
                            <Typography variant="body2">
                              {buildingType.key}: {buildingType.value}
                            </Typography>
                          </div>
                        ))}
                      </>
                    }
                  />
                )}
              </div>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
        type: 'string',
      },
      {
        field: 'warnings',
        type: 'actions',
        aggregable: false,
        hideable: false,
        groupable: false,
        minWidth: 60,
        maxWidth: 60,
        renderCell: (params) => {
          if (isGroupRow(params)) {
            const children = params.rowNode.children?.map((childId) => {
              const child = params.api.getRow(childId);
              return child;
            });
            if (children.some((r) => !r.meter_in_sync)) {
              return (
                <Tooltip title={t('warnings.rowOutdated')} placement="top">
                  <SyncRoundedIcon
                    color="info"
                    sx={{
                      cursor: 'pointer',
                      marginLeft: 1,
                      animation: 'spin 2s linear infinite',
                      '@keyframes spin': {
                        '0%': {
                          transform: 'rotate(360deg)',
                        },
                        '100%': {
                          transform: 'rotate(0deg)',
                        },
                      },
                    }}
                  />
                </Tooltip>
              );
            } else {
              return null;
            }
          }

          if (!params.row.meter_in_sync) {
            return (
              <Tooltip title={t('warnings.rowOutdated')} placement="top">
                <SyncRoundedIcon
                  color="info"
                  sx={{
                    cursor: 'pointer',
                    marginLeft: 1,
                    animation: 'spin 2s linear infinite',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(360deg)',
                      },
                      '100%': {
                        transform: 'rotate(0deg)',
                      },
                    },
                  }}
                />
              </Tooltip>
            );
          }
        },
      },
    ];

    return cols.map((column) => {
      const origValueGetter = column.valueGetter;
      return {
        flex: 1,
        type: 'number',
        minWidth: 140,
        groupable: false,
        ...column,
        renderHeader: (params) => (
          <CustomHeaderWithLabel props={params} t={t} key={t.ns} />
        ),
        headerName: t(`columns.${column.field}.header`),

        valueGetter: (value, row, column, api) => {
          return valueGetterWrapper(
            value,
            row,
            column,
            api,
            origValueGetter,
            matrikelAnalysisData,
            'combined',
          );
        },
        cellClassName: firstColumns.includes(column.field)
          ? 'cell-left-border'
          : '',
      };
    }) as GridColDef[];
  }, [
    benchmarkData?.BenchmarkData,
    firstColumns,
    matrikelAnalysisData,
    t,
    transformedData.portfolio_analysis_list,
  ]);
  return columns;
};
